import type { ComponentType, SVGAttributes } from "react";
import {
  AudienceIcon,
  BreakdownIcon,
  BuildingIcon,
  MetricIcon,
  TimeIcon,
} from "@hightouch/htui";
import { ContextFileType } from "src/graphql/types";

/**
 * A starter context template surfaced when a user adds new context. Each one is
 * a "blueprint" for a kind of business knowledge the data agent benefits from.
 *
 * The goal is threefold:
 *  1. Show customers the *kind* of information we expect them to provide.
 *  2. Motivate them to replace the placeholder prompts with their real rules.
 *  3. Inspire additional, customer-specific context beyond what we ship.
 *
 * These are optional starting points — agents already understand a lot from the
 * schema, Customer Studio, etc. — so the copy avoids asking for anything we can
 * already infer (e.g. how entities map to models).
 *
 * `name` becomes the context entry's name, `description` becomes its
 * description (and doubles as the "when to use this" copy on the gallery card),
 * and `markdown` is the starter body the user edits.
 */
export type ContextStarterTemplate = {
  id: string;
  name: string;
  description: string;
  icon: ComponentType<SVGAttributes<SVGElement>>;
  markdown: string;
  /**
   * Pre-classifies the seeded entry when a matching context-file type exists.
   * Left undefined (→ unclassified) for templates without a clear mapping; the
   * user can classify it themselves.
   */
  contextType?: ContextFileType;
};

const REPLACE_HINT =
  "> Optional — fill in only what's specific to your business. Replace the italicized prompts below and delete anything that doesn't apply.";

export const CONTEXT_STARTER_TEMPLATES: ContextStarterTemplate[] = [
  {
    id: "business-context",
    name: "Business context",
    description:
      "Use for any question about your company — what you do, how you make money, your products, and your marketing goals.",
    icon: BuildingIcon,
    contextType: ContextFileType.BusinessContext,
    markdown: `# Business context

${REPLACE_HINT}

- **What we do:** _Describe your company and the core problem you solve for customers._
- **How we make money:** _Your revenue model (subscriptions, one-time purchases, marketplace fees, ads, etc.)._
- **Products & offerings:** _Your main products, plans, or services and how they differ._
- **Value proposition:** _Why customers choose you over the alternatives._
- **Customers & market:** _Who your customers are (B2B, B2C, key segments, geographies)._
- **Marketing goals:** _Your high-level goals (acquisition, retention, expansion, reactivation) and current priorities._
`,
  },
  {
    id: "metrics-analysis",
    name: "Metric definitions & analysis guidelines",
    description:
      "Use when calculating metrics or analyzing performance — defines your key terms, how metrics are computed, records to exclude, and lifecycle/campaign context.",
    icon: MetricIcon,
    markdown: `# Metric definitions & analysis guidelines

${REPLACE_HINT}

## Key definitions
- **Core terms:** _What "user," "customer," and "account" mean in your business, and how they differ._
- **Conversion / transaction:** _What counts as a conversion — purchase, signup, booking, subscription start?_
- **Active user:** _The exact qualifying event and recency window (e.g. "purchased in the last 90 days")._

## Metric definitions
- **Retention rate:** _How it's calculated, what cohort, over what window._
- **Churn:** _Voluntary vs. involuntary, and how it's identified in the data._
- **AOV / LTV / conversion rate:** _Numerator, denominator, and what's included or excluded._
- **Look-alike metrics:** _Metrics that sound similar but differ (e.g. "installs" vs. "activations")._
- **Calculated elsewhere:** _Metrics that come from a BI tool and shouldn't be recomputed — or must be computed a specific way._

## Analysis guidelines
- **Records to exclude:** _Test/internal accounts, bots, refunded or zero-value transactions, etc._
- **Known data caveats:** _Bad data periods, or fields that are often empty and what that means._
- **Lifecycle stages:** _Your stages and what moves a user between them, if relevant to reporting._
- **Campaign types:** _How you categorize campaigns (transactional vs. promotional vs. lifecycle) and how engagement is measured per channel._
`,
  },
  {
    id: "audience-building",
    name: "Audience building guidelines",
    description:
      "Use when building or sizing audiences — defines who's eligible to message, required consent/exclusions, and reusable building blocks.",
    icon: AudienceIcon,
    markdown: `# Audience building guidelines

${REPLACE_HINT}

- **Marketable base:** _Who is eligible to message (e.g. opted-in, active account, not suppressed)._
- **Required exclusions:** _Filters that must always apply before a send (unsubscribes, bounces, legal holds)._
- **Reusable building blocks:** _Existing audiences or segments to use as starting points for new ones._
- **Suppression lists:** _Where they live (your ESP vs. Hightouch) and how they're applied._
- **Frequency capping:** _Any limits (e.g. max 1 message per user per 7 days)._
- **Compliance review:** _Which audiences require review before activation._
`,
  },
  {
    id: "time-fiscal-calendar",
    name: "Fiscal calendar & time",
    description:
      "Use for any time-based question — defines your fiscal calendar, time zone, and how to interpret relative dates.",
    icon: TimeIcon,
    markdown: `# Fiscal calendar & time

${REPLACE_HINT}

- **Fiscal year:** _Start and end (e.g. April 1 – March 31, or standard Jan–Dec)._
- **Quarters & weeks:** _How quarters map to months, and whether you use a 4-4-5, 4-5-4, or standard calendar._
- **Relative terms:** _How to interpret "last month," "YTD," "this quarter," and "prior period."_
- **Default time zone:** _The time zone that applies to reporting._
- **Week start day:** _Sunday or Monday._
- **Out-of-range requests:** _What agents should do when asked for a period with no data._
`,
  },
  {
    id: "channels-attribution",
    name: "Channels & attribution",
    description:
      "Use for questions about acquisition, revenue, or channel performance — tells agents where to read campaign engagement and attribution data (the warehouse vs. the ad platforms).",
    icon: BreakdownIcon,
    markdown: `# Channels & attribution

${REPLACE_HINT}

Agents don't build attribution models — they read your existing attribution data. The most important thing to tell them is **where** campaign engagement and conversion/attribution data lives and how to join it.

## Ad channels
Campaign attribution exists at two levels:
- **Platform-reported:** _Which ad platforms report spend, impressions, engagements, and platform-attributed conversions (using each platform's own attribution model and window)?_
- **Warehouse attribution:** _Do you track downstream conversions yourself and store them in the warehouse? If so, which table(s)?_
- **Joining the two:** _How should agents join warehouse attribution to platform-reported metrics — e.g. by campaign ID?_

## Lifecycle
- **Source of truth:** _For lifecycle, conversion data and campaign attribution come from the warehouse — which table(s) should agents read?_

## General
- **Channels & labels:** _Your acquisition/marketing channels and how each is labeled in the data._
- **Unattributed traffic:** _How to handle direct / (none) traffic in analysis._
`,
  },
];
